import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { ChevronDown, ChevronUp, Filter, RefreshCw } from "lucide-react";
import complianceApi from "../../api/complianceApi";

const MONTHS = ["Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar"];
const QUARTERS = ["Q1", "Q2", "Q3", "Q4"];

const STATUS_COLORS = {
  FILED: { bg: "bg-emerald-100", text: "text-emerald-700", icon: "🟢", label: "Filed" },
  VERIFIED: { bg: "bg-blue-100", text: "text-blue-700", icon: "🔵", label: "Verified" },
  PENDING: { bg: "bg-red-100", text: "text-red-700", icon: "🔴", label: "Pending" },
  NOT_DUE: { bg: "bg-gray-100", text: "text-gray-400", icon: "⚪", label: "Not Due" },
  OVERDUE: { bg: "bg-orange-100", text: "text-orange-700", icon: "🟠", label: "Overdue" },
  BOOKED: { bg: "bg-purple-100", text: "text-purple-700", icon: "🟣", label: "Booked" },
};

const getCurrentFiscalYear = () => {
  const now = new Date();
  const year = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return `FY${year}-${(year + 1).toString().slice(-2)}`;
};

const ComplianceTrackerGrid = () => {
  const token = useSelector((state) => state.auth.access_token);
  const [loading, setLoading] = useState(false);
  const [trackerData, setTrackerData] = useState([]);
  const [category, setCategory] = useState("GST");
  const [fiscalYear, setFiscalYear] = useState(getCurrentFiscalYear());
  const [viewType, setViewType] = useState("monthly"); // monthly or quarterly
  const [expandedRows, setExpandedRows] = useState({});

  const fiscalYears = [
    "FY2025-26",
    "FY2024-25",
    "FY2023-24",
  ];

  const categories = [
    { value: "GST", label: "GST Compliance" },
    { value: "TDS", label: "TDS Compliance" },
    { value: "INCOME_TAX", label: "Income Tax" },
    { value: "LEGAL", label: "Legal Compliance" },
  ];

  const fetchTrackerData = async () => {
    setLoading(true);
    try {
      const response = await complianceApi.getAllAccountsTracker(category, fiscalYear, token);
      setTrackerData(response || []);
    } catch (error) {
      console.error("Error fetching compliance tracker:", error);
      setTrackerData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchTrackerData();
    }
  }, [token, category, fiscalYear]);

  const toggleRow = (accountId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [accountId]: !prev[accountId],
    }));
  };

  const getStatusForPeriod = (periods, periodName) => {
    if (!periods) return STATUS_COLORS.NOT_DUE;
    const period = periods.find((p) => p.period === periodName);
    if (!period) return STATUS_COLORS.NOT_DUE;
    return STATUS_COLORS[period.status] || STATUS_COLORS.NOT_DUE;
  };

  const getPeriodDetails = (periods, periodName) => {
    if (!periods) return null;
    return periods.find((p) => p.period === periodName);
  };

  const periodsToShow = viewType === "monthly" ? MONTHS : QUARTERS;

  // Calculate summary stats
  const totalAccounts = trackerData.length;
  const accountsWithPending = trackerData.filter((acc) =>
    acc.periods?.some((p) => p.status === "PENDING" || p.status === "OVERDUE")
  ).length;
  const fullyCompliant = trackerData.filter((acc) =>
    acc.periods?.every((p) => p.status === "FILED" || p.status === "NOT_DUE" || p.status === "VERIFIED")
  ).length;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-slate-200">
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-slate-800">
              {category === "GST" ? "GST" : category === "TDS" ? "TDS" : category} Compliance Tracker
            </h3>
            <p className="text-sm text-slate-500">
              {fiscalYear} • {viewType === "monthly" ? "Monthly" : "Quarterly"} View
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {/* Category Filter */}
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="px-3 py-1.5 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              {categories.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>

            {/* Fiscal Year Filter */}
            <select
              value={fiscalYear}
              onChange={(e) => setFiscalYear(e.target.value)}
              className="px-3 py-1.5 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              {fiscalYears.map((fy) => (
                <option key={fy} value={fy}>
                  {fy}
                </option>
              ))}
            </select>

            {/* View Type Toggle */}
            <div className="flex rounded-lg border border-slate-300 overflow-hidden">
              <button
                onClick={() => setViewType("monthly")}
                className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                  viewType === "monthly"
                    ? "bg-indigo-600 text-white"
                    : "bg-white text-slate-600 hover:bg-slate-50"
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setViewType("quarterly")}
                className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                  viewType === "quarterly"
                    ? "bg-indigo-600 text-white"
                    : "bg-white text-slate-600 hover:bg-slate-50"
                }`}
              >
                Quarterly
              </button>
            </div>

            {/* Refresh Button */}
            <button
              onClick={fetchTrackerData}
              disabled={loading}
              className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="flex flex-wrap gap-4 mt-3">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-emerald-500"></span>
            <span className="text-sm text-slate-600">
              Compliant: <strong className="text-emerald-600">{fullyCompliant}</strong>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-red-500"></span>
            <span className="text-sm text-slate-600">
              Pending: <strong className="text-red-600">{accountsWithPending}</strong>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-slate-400"></span>
            <span className="text-sm text-slate-600">
              Total Accounts: <strong>{totalAccounts}</strong>
            </span>
          </div>
        </div>
      </div>

      {/* Grid Table */}
      <div className="overflow-x-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : trackerData.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            No compliance data available for the selected filters
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left py-3 px-4 font-semibold text-slate-700 sticky left-0 bg-slate-50 min-w-[200px]">
                  Client
                </th>
                {periodsToShow.map((period) => (
                  <th
                    key={period}
                    className="text-center py-3 px-2 font-semibold text-slate-700 min-w-[60px]"
                  >
                    {period}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {trackerData.map((account) => (
                <React.Fragment key={account.accountId}>
                  <tr
                    className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                    onClick={() => toggleRow(account.accountId)}
                  >
                    <td className="py-3 px-4 sticky left-0 bg-white">
                      <div className="flex items-center gap-2">
                        {expandedRows[account.accountId] ? (
                          <ChevronUp size={16} className="text-slate-400" />
                        ) : (
                          <ChevronDown size={16} className="text-slate-400" />
                        )}
                        <div>
                          <div className="font-medium text-slate-800">
                            {account.accountName}
                          </div>
                          <div className="text-xs text-slate-500">
                            {account.complianceType}
                          </div>
                        </div>
                      </div>
                    </td>
                    {periodsToShow.map((period) => {
                      const status = getStatusForPeriod(account.periods, period);
                      return (
                        <td key={period} className="text-center py-3 px-2">
                          <div
                            className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${status.bg}`}
                            title={status.label}
                          >
                            <span className="text-sm">{status.icon}</span>
                          </div>
                        </td>
                      );
                    })}
                  </tr>

                  {/* Expanded Row Details */}
                  {expandedRows[account.accountId] && (
                    <tr className="bg-slate-50">
                      <td colSpan={periodsToShow.length + 1} className="py-3 px-4">
                        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
                          {periodsToShow.map((period) => {
                            const details = getPeriodDetails(account.periods, period);
                            const status = getStatusForPeriod(account.periods, period);
                            return (
                              <div
                                key={period}
                                className={`p-3 rounded-lg border ${status.bg} border-slate-200`}
                              >
                                <div className="font-medium text-slate-800 mb-1">
                                  {period}
                                </div>
                                <div className={`text-sm font-medium ${status.text}`}>
                                  {status.label}
                                </div>
                                {details && details.documentCount > 0 && (
                                  <div className="text-xs text-slate-500 mt-1">
                                    {details.documentCount} document(s)
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Legend */}
      <div className="px-4 py-3 border-t border-slate-200 bg-slate-50">
        <div className="flex flex-wrap items-center gap-4 text-xs">
          <span className="font-medium text-slate-600">Legend:</span>
          {Object.entries(STATUS_COLORS).map(([key, value]) => (
            <div key={key} className="flex items-center gap-1">
              <span>{value.icon}</span>
              <span className="text-slate-600">{value.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ComplianceTrackerGrid;
